import React, { useEffect, useRef, useState, Children } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { animations } from './animations'
import type { StickyStepperProps } from './types'

// Register ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

export const StickyStepper: React.FC<StickyStepperProps> = ({
  height = '400vh',
  pinOffset = 0,
  animation = 'fade',
  onStepChange,
  children
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [isInView, setIsInView] = useState(false)
  
  const steps = Children.toArray(children)
  const stepCount = steps.length

  // Intersection Observer for viewport detection
  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting)
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    )

    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current || !contentRef.current || !isInView) {
      return
    }

    const container = containerRef.current
    const content = contentRef.current
    const stepElements = content.querySelectorAll('.sticky-step')

    // Set up the pinning animation
    const scrollHeight = container.getAttribute('data-scroll-height') || height
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: `top+=${pinOffset} top`,
        end: `+=${scrollHeight}`,
        pin: content,
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress
          const newStepIndex = Math.min(
            Math.floor(progress * stepCount),
            stepCount - 1
          )
          
          if (newStepIndex !== currentStep) {
            setCurrentStep(newStepIndex)
            onStepChange?.(newStepIndex)
          }
        }
      }
    })

    // Animate steps - strict separation, no overlap, longer duration
    stepElements.forEach((step, index) => {
      const stepElement = step as HTMLElement
      
      // Each step gets equal time with clear separation
      const stepStart = index / stepCount
      const stepEnd = (index + 1) / stepCount
      const stepDuration = stepEnd - stepStart
      
      // Much shorter transition times for clean separation
      const transitionTime = stepDuration * 0.05 // Only 5% for transitions
      const visibleTime = stepDuration * 0.9 // 90% visible time
      
      const fadeInStart = stepStart
      const fadeInEnd = stepStart + transitionTime
      const visibleStart = fadeInEnd
      const visibleEnd = stepEnd - transitionTime
      const fadeOutStart = visibleEnd
      
      // Reset all steps to invisible
      gsap.set(stepElement, { opacity: 0 })
      
      // Quick fade in
      tl.to(stepElement, {
        opacity: 1,
        duration: transitionTime,
        ease: 'power2.out'
      }, fadeInStart)
      
      // Apply custom animation right after fade in
      if (animation !== 'none' && animation in animations) {
        tl.add(() => {
          animations[animation](stepElement)
        }, fadeInEnd)
      }
      
      // Keep visible for long duration (no animation, just hold)
      tl.to({}, { duration: visibleTime }, visibleStart)
      
      // Quick fade out (only if not the last step)
      if (index < stepCount - 1) {
        tl.to(stepElement, {
          opacity: 0,
          duration: transitionTime,
          ease: 'power2.in'
        }, fadeOutStart)
      }
    })

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
  }, [height, pinOffset, animation, onStepChange, stepCount, currentStep, isInView])

  return (
    <div 
      ref={containerRef}
      style={{ 
        height: '100vh', // Container sadece 1 viewport height
        position: 'relative',
        background: '#000000'
      }}
      data-scroll-height={height} // Scroll height'ı data attribute olarak sakla
    >
      <div 
        ref={contentRef}
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'hidden',
          background: '#000000'
        }}
      >
        {/* Only render content when in view */}
        {isInView && (
          <>
            {children}
            
            {/* Apple-style Progress Dots */}
            <div 
              className="progress-dots"
              style={{
                position: 'fixed',
                right: '2rem',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                zIndex: 1000
              }}
            >
              {steps.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: currentStep === index ? '12px' : '8px',
                    height: currentStep === index ? '12px' : '8px',
                    borderRadius: '50%',
                    backgroundColor: currentStep === index ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    cursor: 'pointer',
                    border: currentStep === index ? '2px solid rgba(255, 255, 255, 0.3)' : 'none',
                    boxShadow: currentStep === index ? '0 0 20px rgba(255, 255, 255, 0.3)' : 'none'
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}